import { useEffect, useMemo, useState, MouseEvent, useCallback } from 'react';
import classNames from 'classnames';

import { useExplorationDataQuery, getNodes, mapSort } from '@/graphql';
import {
  Breakpoint,
  useIsInView,
  useLoadingStatus,
  useInitialQueryLoadCondition,
  useScreenSize,
} from '@/lib/hooks';
import { ErrorBoundary, GenericFallback } from '@/lib/error';
import { useAccountContext } from '@/lib/accounts/context';
import { useTrackEvent } from '@/lib/analytics';
import { isRecordsCell, setViewOptions } from '@/core/cell';
import { useToastContext } from '@/components/toast';
import { Tooltip } from '@/components/tooltip';
import { HideInEmbedded } from '@/components/layout/hide-in-embedded';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';
import { Loader } from '@/components/loader';
import { DropdownIconSizePx } from '@/components/dropdown';

import { shouldPropagateEvent } from '@/explore/utils/interactable';

import { useExplorationContext } from './exploration-context';
import { useExplorationCellContext } from './exploration-cell-context';
import { useRefetchCellDataContext } from './refetch-cell-data-context';
import { EditColumns } from '../components/edit-columns';
import { Visualisations } from '../components/visualisations';
import { convertRecordTypeTypes } from '../input';
import {
  addExplorationUrlBuilders,
  dereferenceVariablesInPipeline,
  filterVariablesForPipeline,
} from '../utils';
import { setFieldIndex } from '../utils/field-ordering';
import { sortProperties } from '../utils/field-ordering';
import { detectAggregatedProperties } from '../utils/drilldown';
import {
  Exploration,
  RecordsCell,
  ExplorationParameters,
  SortItem,
  CellViewOptions,
} from '../types';
import { useMetadataContext } from '../metadata-context';
import { Button } from '../../components/button';
import { Icon } from '../../components/icon';
import { useExplorationUIContext } from './exploration-ui-context';
import { PaginatedRecords } from '../components/paginated-records';
import { AlertFormModal } from '../../settings/alerts/edit-alert';
import { MasterBadge } from '../components/master-badge';
import { PipelinePreview } from './pipeline-preview';
import { dereferencePipeline, getBaseModelName, getParentPipelineCell } from '../pipeline/utils';
import { CollapsibleContainer, CollapseButton, CollapsibleContent } from './collapsible-cell';
import { CellControls, CellOption } from './cell-controls';
import { CellTitle } from './cell-title';
import { HorizontalScrollTable } from '../components/horizontal-scroll-table';
import { CardsList } from '../components/cards-list';
import { containsInvalidOperations } from '../pipeline/operation';
import { getModel } from '../model/utils';
import { SqlPreview } from './sql-preview';
import { YamlPreview } from './yaml-preview';
import { getFinalStateOrThrow } from '../pipeline/state';
import { convertRecordsToCsv } from './utils/csv';
import { usePipelineDataLazyQuery, useRefetchCellData } from './hooks';
import { downloadCsv, generateTimestampedFilename } from '../utils/download';

import { QueryError } from '../components/error';

import style from './exploration.module.scss';
import table from '../../components/table/table.module.scss';

const CsvExportRecordsLimit = 2000; // Use maximum batch size to prevent pagination. This is a temporary solution until we have a proper export feature.

interface RecordsCellViewProps {
  cell: RecordsCell;
  exploration: Exploration;
  className?: string;
  parameters: ExplorationParameters;
  height?: number;
  isDraggable?: boolean;
  onSelectCell?: () => void;
  onSetDraggable: (value: boolean) => void;
}

export const RecordsCellView = (props: RecordsCellViewProps) => {
  const { isDraggable = true } = props;

  const trackEvent = useTrackEvent();

  const { exploration, createCellInstance, getVariables } = useExplorationContext();
  const { selectCell, scrollToCell, openVisualisationEditor } = useExplorationUIContext();
  const {
    cell,
    setCell,
    showTable,
    hideTable,
    isTableVisible,
    isConversationPart,
    rowContainsConversationPart,
    addDefaultVisualization,
    startConversation,
    isCollapsible,
    isCollapsed,
    isPipelinePreviewVisible,
    isBlockTitleVisible,
    isConversationSubject,
    getYaml,
  } = useExplorationCellContext();
  const { refetchAll } = useRefetchCellDataContext();

  const { account, hasPermission } = useAccountContext();
  const screenSize = useScreenSize();
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const [isShowingSql, setIsShowingSql] = useState(false);
  const [isShowingYaml, setIsShowingYaml] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDragHovered, setIsDragHovered] = useState(false);
  const [containerRef, isInView] = useIsInView();
  const addToast = useToastContext();
  const { fetch: fetchPipelineData } = usePipelineDataLazyQuery({
    cachePolicy: 'no-cache',
  });

  const handleCreateCellInstance = () => {
    const { cell: cellInstance } = createCellInstance(cell.id);

    if (cellInstance !== undefined) {
      selectCell(cellInstance.id);
      scrollToCell(cellInstance.id);
    }

    trackEvent('Exploration Cell Instance Created', {
      explorationId: exploration.explorationId,
      name: exploration.name,
      cell,
    });
  };

  const handleSetIsDragHovered = (value: boolean) => {
    props.onSetDraggable(value);
    setIsDragHovered(value);
  };

  const handleDownloadCsv = async () => {
    if (isRecordsCell(cell)) {
      setIsDownloadingCsv(true);
      try {
        const dereferencedPipeline = dereferencePipeline(cell.pipeline, exploration);

        const { records, fields, hasMore } = await fetchPipelineData({
          accountId: account.accountId,
          pipeline: dereferencedPipeline,
          sort: mapSort(cell.sort),
          variables: filterVariablesForPipeline(dereferencedPipeline, getVariables()),
          first: CsvExportRecordsLimit,
        });

        const csvString = convertRecordsToCsv(
          records,
          fields.filter(({ key }) => !(cell.excludeProperties?.includes(key) ?? false)),
          hasMore,
        );
        const filename = generateTimestampedFilename(
          cell.title ?? dereferencedPipeline.baseModelId,
        );

        downloadCsv(csvString, filename);
        trackEvent('CSV Downloaded', {
          exploration,
          cell,
        });
      } catch {
        addToast({
          title: `Error downloading CSV`,
          content: () =>
            `An unexpected error occurred while downloading the CSV file. Our team has been notified.`,
          kind: 'error',
        });
      }
      setIsDownloadingCsv(false);
    }
  };

  const handleAddVisualisation = (event: MouseEvent) => {
    (event.currentTarget as HTMLElement)?.blur();
    const visualisationsLength = props.cell.visualisations?.length ?? 0;
    addDefaultVisualization();
    visualisationsLength > 0 && openVisualisationEditor(visualisationsLength);
  };

  const editButtonVisible = screenSize.breakpoint <= Breakpoint.md;
  const pipelineContainsInvalidOperations = containsInvalidOperations(
    props.cell.pipeline.operations,
  );

  const cellControlOptions = (defaultOptions: CellOption[]): CellOption[] => [
    ...defaultOptions,
    {
      label: 'Ask about this block',
      icon: <Icon name="Zap" size={DropdownIconSizePx} />,
      onClick: startConversation,
      sort: 1,
      disabled: isConversationPart || rowContainsConversationPart,
      tooltip: isConversationPart
        ? 'This block is already part of a conversation'
        : rowContainsConversationPart
          ? 'Resolve the current conversation or move the block to a separate row first'
          : undefined,
    },
    {
      type: 'divider',
      sort: 1,
    },
    {
      label: 'Duplicate as instance',
      icon: <Icon name="Instance" size={DropdownIconSizePx} />,
      onClick: handleCreateCellInstance,
      sort: 15,
    },
    {
      label: 'Reload data',
      icon: <Icon name="RefreshCw" size={DropdownIconSizePx} />,
      onClick: refetchAll,
      sort: 50,
    },
    isDownloadingCsv
      ? {
          label: 'Downloading CSV...',
          icon: <Loader size="small" type="spinner-dark" />,
          disabled: true,
          sort: 25,
        }
      : {
          label: 'Download CSV',
          icon: <Icon name="Download" size={DropdownIconSizePx} />,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDownloadCsv();
          },
          sort: 25,
        },
    { type: 'divider', sort: 30 },
    {
      label: 'Show YAML',
      icon: <Icon name="Code" size={DropdownIconSizePx} />,
      onClick: () => setIsShowingYaml(true),
      sort: 36,
    },
    {
      label: 'Show SQL',
      icon: <Icon name="Database" size={DropdownIconSizePx} />,
      onClick: () => setIsShowingSql(true),
      sort: 38,
    },
    {
      type: 'divider',
      sort: 40,
    },
    ...(hasPermission('MANAGE_ALERTS') && !isConversationPart
      ? ([
          {
            label: 'Create alert',
            icon: <Icon name="Alert" size={DropdownIconSizePx} />,
            onClick: () => setIsCreatingAlert(true),
            sort: 41,
            disabled: containsInvalidOperations(props.cell.pipeline.operations),
          },
          {
            type: 'divider',
            sort: 41,
          },
        ] as CellOption[])
      : []),
    {
      type: 'divider',
      sort: 50,
    },
    {
      label: isTableVisible ? 'Hide table' : 'Show table',
      icon: <Icon name={isTableVisible ? 'EyeOff' : 'Eye'} size={DropdownIconSizePx} />,
      onClick: () => (isTableVisible ? hideTable() : showTable()),
      sort: 51,
      disabled: (props.cell.visualisations?.length ?? 0) === 0,
    },
    {
      label: 'Show/hide columns',
      icon: <Icon name="Edit3" size={DropdownIconSizePx} />,
      onClick: () => setIsEditingColumns(true),
      sort: 55,
    },
  ];

  return (
    <CollapsibleContainer
      onClick={(event) => shouldPropagateEvent(event) && props.onSelectCell?.()}
      ref={containerRef}>
      {isBlockTitleVisible ? (
        <div className={classNames(style.cellHeader)}>
          <div className={style.cellControlsContainer}>
            <HideInEmbedded>
              {isDraggable && !isConversationPart && (
                <Icon
                  name="DragHandle"
                  size={10}
                  className={style.dragHandle}
                  onMouseOver={() => handleSetIsDragHovered(true)}
                  onMouseOut={() => handleSetIsDragHovered(false)}
                />
              )}
            </HideInEmbedded>
            <MasterBadge exploration={props.exploration} pipeline={props.cell.pipeline} />
            <CellTitle
              exploration={props.exploration}
              value={props.cell.title ?? '(Untitled)'}
              onChange={(value) => setCell({ ...props.cell, title: value })}
            />
            <CellControls
              exploration={props.exploration}
              editButtonVisible={editButtonVisible}
              options={cellControlOptions}>
              <HideInEmbedded>
                {!isConversationPart && !rowContainsConversationPart && !isConversationSubject && (
                  <Button
                    icon={<Icon name="Zap" size={12} />}
                    size="compact"
                    variant="gray"
                    title="Ask about this block"
                    onClick={startConversation}>
                    Ask AI
                  </Button>
                )}
                <Button
                  icon={<Icon name="Graphic" />}
                  size="compact"
                  variant="gray"
                  title="Add chart"
                  onClick={handleAddVisualisation}>
                  Add chart
                </Button>
              </HideInEmbedded>
            </CellControls>

            {isCollapsible && <CollapseButton />}
          </div>
          {pipelineContainsInvalidOperations && (!isPipelinePreviewVisible || isCollapsed) && (
            <Tooltip
              placement="right"
              content={
                <>
                  <strong>Block may return incorrect data.</strong>
                  <br />
                  Please review the pipeline of this block and correct the steps marked as invalid.
                </>
              }>
              <div
                className={classNames(style.invalidPipelineWarning, {
                  [style.alignTop]: isCollapsed,
                })}>
                <Icon name={'Alert'} size={DropdownIconSizePx} />
              </div>
            </Tooltip>
          )}
        </div>
      ) : (
        <>
          <HideInEmbedded>
            {isDraggable && !isConversationPart && (
              <div className={style.floatingDragHandle}>
                <Icon
                  name="DragHandle"
                  size={10}
                  className={style.dragHandle}
                  onMouseOver={() => handleSetIsDragHovered(true)}
                  onMouseOut={() => handleSetIsDragHovered(false)}
                />
              </div>
            )}
          </HideInEmbedded>
          <div className={style.floatingCellControls}>
            <CellControls
              exploration={props.exploration}
              editButtonVisible={editButtonVisible}
              options={cellControlOptions}
            />
          </div>
        </>
      )}

      <CollapsibleContent isDragHovered={isDragHovered}>
        <ErrorBoundary fallback={(errorData) => <GenericFallback {...errorData} />}>
          {isShowingSql && (
            <SqlPreview
              pipeline={props.cell.pipeline}
              exploration={props.exploration}
              sort={props.cell.sort}
              onClose={() => setIsShowingSql(false)}
            />
          )}
          {isShowingYaml && (
            <YamlPreview yaml={getYaml()} onClose={() => setIsShowingYaml(false)} />
          )}
          <RecordsCellViewInner
            {...props}
            isEditingColumns={isEditingColumns}
            setIsEditingColumns={setIsEditingColumns}
            isCreatingAlert={isCreatingAlert}
            setIsCreatingAlert={setIsCreatingAlert}
            isInView={isInView}
          />
        </ErrorBoundary>
      </CollapsibleContent>
    </CollapsibleContainer>
  );
};

interface RecordsCellViewInnerProps extends RecordsCellViewProps {
  isEditingColumns: boolean;
  setIsEditingColumns: (value: boolean) => void;
  isCreatingAlert: boolean;
  setIsCreatingAlert: (value: boolean) => void;
  isInView: boolean;
  height?: number;
}

const RecordsCellViewInner = (props: RecordsCellViewInnerProps) => {
  const sort = useMemo(() => mapSort(props.cell.sort), [props.cell.sort]);

  const { account, isFeatureEnabled } = useAccountContext();
  const { explorations, models, metrics } = useMetadataContext();
  const { exploration, getVariables } = useExplorationContext();
  const { openEditor, openVisualisationEditor, openEditForm, scrollToCell } =
    useExplorationUIContext();
  const {
    setCell,
    cell,
    setQueryMeta,
    tableMode,
    isTableVisible,
    getCellViewOption,
    pipelineWithRollback,
  } = useExplorationCellContext();

  const { isCollapsed, isPipelinePreviewVisible, isBlockTitleVisible } =
    useExplorationCellContext();

  const rollbackIndex = (getCellViewOption('rollback') as CellViewOptions['rollback'] | undefined)
    ?.index;

  const dereferencedPipeline = useMemo(
    () => dereferencePipeline(pipelineWithRollback ?? props.cell.pipeline, props.exploration),
    [pipelineWithRollback, props.cell.pipeline, props.exploration],
  );
  const variables = useMemo(
    () => filterVariablesForPipeline(dereferencedPipeline, getVariables()),
    [dereferencedPipeline, getVariables],
  );
  const stateContext = useMemo(
    () => ({
      models,
      variables,
      metrics,
      exploration: props.exploration,
    }),
    [models, variables, metrics, props.exploration],
  );

  const pipelineState = useMemo(
    () => getFinalStateOrThrow(pipelineWithRollback ?? props.cell.pipeline, stateContext),
    [pipelineWithRollback, props.cell.pipeline, stateContext],
  );
  const baseModelName = getBaseModelName(props.cell.pipeline, props.exploration, models);

  const [onCompleted, onError, skipInitialLoad] = useInitialQueryLoadCondition(props.isInView);

  const { data, loading, error, fetchMore, refetch } = useExplorationDataQuery({
    variables: useMemoizeDataQueryVariables({
      accountId: account.accountId,
      baseModelId: dereferencedPipeline.baseModelId,
      pipeline: dereferencedPipeline.operations,
      sort,
      variables,
      options: {
        labels: { exploration: exploration.explorationId, block: cell.id },
        optimize: isFeatureEnabled('optimizeQuery'),
      },
    }),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    skip: skipInitialLoad || !isTableVisible || isCollapsed,
    onCompleted,
    onError,
  });
  useRefetchCellData(refetch);

  const grouping = useMemo(() => data?.account?.query?.grouping ?? [], [data]);
  const groupedKeys = useMemo(() => (grouping ?? []).map(({ key }) => key), [grouping]);

  const recordType = useMemo(
    () => convertRecordTypeTypes(data?.account?.query?.recordType ?? []),
    [data],
  );

  useEffect(
    () => setQueryMeta({ recordType, grouping }),
    [grouping, loading, recordType, setQueryMeta],
  );

  const handleFetchMore = useCallback(async () => {
    if (loading || !(data?.account?.query?.pageInfo.hasNextPage ?? false)) {
      return;
    }

    const after = data?.account?.query?.pageInfo.endCursor;

    fetchMore({
      variables: {
        accountId: account.accountId,
        baseModelId: dereferencedPipeline.baseModelId,
        pipeline: dereferencedPipeline.operations,
        sort,
        after,
      },
    });
  }, [
    account.accountId,
    data?.account?.query?.pageInfo.endCursor,
    data?.account?.query?.pageInfo.hasNextPage,
    dereferencedPipeline.baseModelId,
    dereferencedPipeline.operations,
    fetchMore,
    loading,
    sort,
  ]);

  const fieldOrder = useMemo(() => getCellViewOption('fieldOrder'), [getCellViewOption]);
  const allProperties = useMemo(
    () =>
      sortProperties(
        detectAggregatedProperties(
          addExplorationUrlBuilders(pipelineState.fields, explorations),
          dereferencedPipeline,
        ),
        fieldOrder,
      ),
    [pipelineState.fields, explorations, dereferencedPipeline, fieldOrder],
  );

  // Grouped columns cannot be excluded and must be visible at all times. Therefore they are always filtered out
  // from excludedProperties. Also they cannot be turned off in EditColumns view.
  const excludedProperties = useMemo(
    () => (props.cell.excludeProperties ?? []).filter((key) => !groupedKeys.includes(key)),
    [props.cell.excludeProperties, groupedKeys],
  );

  const setExcludedColumns = useCallback(
    (properties: string[]) => setCell({ ...props.cell, excludeProperties: properties }),
    [props.cell, setCell],
  );

  const setSort = useCallback(
    (sort: SortItem | null) => setCell({ ...props.cell, sort: sort === null ? undefined : [sort] }),
    [props.cell, setCell],
  );

  const handleReorderField = useCallback(
    (fromIndex: number, toIndex: number, after: boolean) => {
      const fieldKey = allProperties.at(fromIndex)?.key;
      if (fieldKey === undefined) {
        throw new Error('Field key not found for reordering');
      }
      setCell(
        setViewOptions(props.cell, {
          ...props.cell.viewOptions,
          fieldOrder: setFieldIndex(fieldKey, fromIndex, toIndex, after, fieldOrder ?? {}),
        }),
      );
    },
    [allProperties, fieldOrder, props.cell, setCell],
  );

  const loaded = data?.account?.query !== undefined;

  const { isFirstLoad, isSubsequentLoad } = useLoadingStatus(loading, loaded);

  const model = getModel(models, dereferencedPipeline.baseModelId);
  const records = useMemo(() => getNodes(data?.account?.query), [data?.account?.query]);
  const visualisations = props.cell.visualisations ?? [];

  const handleEditVisualisation = (idx: number) => {
    openEditor(cell.id);
    openVisualisationEditor(idx);
  };

  if (error !== undefined) {
    return <QueryError error={error} refetch={refetch} />;
  }

  const isResized = props.height !== undefined;

  return (
    <>
      {props.isEditingColumns && (
        <EditColumns
          properties={allProperties.filter(({ key }) => !groupedKeys.includes(key))}
          excludedColumns={excludedProperties}
          setExcludedColumns={setExcludedColumns}
          onClose={() => props.setIsEditingColumns(false)}
        />
      )}

      {props.isCreatingAlert && (
        <AlertFormModal
          ctx={stateContext}
          integrations={account.integrations}
          alertConfiguration={{
            name: props.cell.title ?? undefined,
            pipeline: dereferenceVariablesInPipeline(dereferencedPipeline, variables),
            keyFields:
              pipelineState.fields.filter((field) => field.pk ?? false).map((field) => field.key) ??
              [],
            excludedFields: props.cell.excludeProperties ?? [],
          }}
          onClose={() => props.setIsCreatingAlert(false)}
          onAlertSaved={() => props.setIsCreatingAlert(false)}
        />
      )}

      {isPipelinePreviewVisible && (
        <div
          className={classNames(style.cellHeaderAccessory, {
            [style.noTitleAbove]: !isBlockTitleVisible,
          })}>
          <PipelinePreview
            title={baseModelName}
            pipeline={props.cell.pipeline}
            exploration={props.exploration}
            variables={variables}
            models={models}
            onClickParent={() =>
              scrollToCell(
                getParentPipelineCell(props.exploration, props.cell.pipeline)?.id ?? null,
              )
            }
            onClickOperation={(index) => {
              openEditor(cell.id);
              openEditForm({ index });
            }}
            rollbackIndex={rollbackIndex}
          />
        </div>
      )}

      {visualisations.length > 0 && (
        <div
          className={classNames(style.cellSection, {
            [style.noBorderTop]: !isBlockTitleVisible && !isPipelinePreviewVisible,
          })}>
          <Visualisations
            visualisations={visualisations}
            onEditVisualisation={handleEditVisualisation}
            isInView={props.isInView}
            isResized={isResized}
          />
        </div>
      )}

      {isTableVisible && (
        <div
          className={classNames(style.cellSection, style.tableSection, {
            [table.groupedTable]: groupedKeys.length > 0,
          })}>
          <PaginatedRecords
            properties={allProperties}
            excludedProperties={excludedProperties}
            setExcludedProperties={setExcludedColumns}
            grouping={groupedKeys}
            sort={sort}
            setSort={setSort}
            reorderField={handleReorderField}
            records={records}
            pipeline={props.cell.pipeline}
            variables={variables}
            cellHeight={props.height}
            defaultPageSize={tableMode === 'cards' ? 3 : 10}
            model={model}
            fetchMore={handleFetchMore}
            footerContent={skipInitialLoad || (isSubsequentLoad && <Loader />)}
            loading={skipInitialLoad || isFirstLoad}
            component={tableMode === 'cards' ? CardsList : HorizontalScrollTable}
          />
        </div>
      )}
    </>
  );
};
